import { useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, Check } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { submitCustomPackaging } from "@/lib/api";

const OPTIONS = {
  material: ["Corrugated", "Rigid Board", "Kraft Paper", "Laminated Film", "HDPE / PP"],
  printing: ["No Printing", "1-Colour", "Full CMYK", "Foil Stamping", "Spot UV"],
  finish: ["Matte", "Gloss", "Soft-touch", "Emboss", "Uncoated"],
};

export default function CustomPackaging() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", material: "Corrugated", dimensions: "", printing: "Full CMYK", finish: "Matte", quantity: "", message: "", artwork_name: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitCustomPackaging(form);
      setDone(true);
      toast.success("Custom packaging request submitted!");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-royal";
  const Pills = ({ field }) => (
    <div className="flex flex-wrap gap-2">
      {OPTIONS[field].map((o) => (
        <button type="button" key={o} onClick={() => set(field, o)} data-testid={`opt-${field}-${o.toLowerCase().replace(/\s|\//g, "-")}`} className={form[field] === o ? "rounded-full px-4 py-2 text-sm border transition-colors bg-navy text-white border-navy" : "rounded-full px-4 py-2 text-sm border transition-colors border-border hover:bg-secondary"}>{o}</button>
      ))}
    </div>
  );
  /* eslint-disable-next-line react/no-unstable-nested-components */

  return (
    <div data-testid="custom-page">
      <PageHeader overline="Build Your Own" title="Design custom packaging, get an instant quote." subtitle="Choose your material, dimensions, printing and finish. Upload your artwork and our team will craft a tailored quotation." />

      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10">
        {done ? (
          <Reveal>
            <div className="rounded-4xl border border-emerald/30 bg-emerald/5 p-12 text-center max-w-2xl mx-auto" data-testid="custom-success">
              <span className="grid place-items-center h-14 w-14 rounded-full bg-emerald text-white mx-auto"><Check size={28} /></span>
              <h3 className="mt-5 font-heading text-2xl font-bold text-foreground">Request received!</h3>
              <p className="mt-2 text-muted-foreground">Our design team will prepare your custom packaging quote and reach out shortly.</p>
            </div>
          </Reveal>
        ) : (
          <form onSubmit={submit} data-testid="custom-form" className="grid lg:grid-cols-2 gap-10">
            <div className="space-y-7">
              <div><h3 className="overline text-royal mb-3">01 · Material</h3><Pills field="material" /></div>
              <div><h3 className="overline text-royal mb-3">02 · Printing</h3><Pills field="printing" /></div>
              <div><h3 className="overline text-royal mb-3">03 · Finish</h3><Pills field="finish" /></div>
              <div><h3 className="overline text-royal mb-3">04 · Dimensions</h3><input placeholder="e.g. 30 x 20 x 10 cm" value={form.dimensions} onChange={(e) => set("dimensions", e.target.value)} className={inputCls} data-testid="c-dimensions" /></div>
              <div><h3 className="overline text-royal mb-3">05 · Quantity</h3><input placeholder="e.g. 1000 units" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className={inputCls} data-testid="c-quantity" /></div>
            </div>
            <div className="rounded-4xl border border-border bg-card p-8 h-fit lg:sticky lg:top-28 space-y-4">
              <h3 className="font-heading text-xl font-bold text-foreground">Your details</h3>
              <input required placeholder="Full name *" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="c-name" />
              <input required type="email" placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="c-email" />
              <input required placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="c-phone" />
              <textarea rows={3} placeholder="Notes" value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} data-testid="c-message" />
              <label className="flex items-center gap-3 rounded-xl border border-dashed border-border px-4 py-4 cursor-pointer hover:bg-secondary/50 transition-colors">
                <UploadCloud size={20} className="text-royal" />
                <span className="text-sm text-muted-foreground">{form.artwork_name || "Upload artwork"}</span>
                <input type="file" className="hidden" onChange={(e) => set("artwork_name", e.target.files?.[0]?.name || "")} data-testid="c-artwork" />
              </label>
              <div className="rounded-xl bg-secondary/60 p-4 text-sm text-muted-foreground font-mono-accent">
                {form.material} · {form.printing} · {form.finish}
              </div>
              <button type="submit" disabled={busy} data-testid="c-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white px-7 py-4 font-semibold hover:bg-royal transition-colors disabled:opacity-60">
                {busy ? <Loader2 size={18} className="animate-spin" /> : "Get Instant Quote"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
